import {
  Button,
  Container,
  Group,
  LoadingOverlay,
  MultiSelect,
  Pagination,
  Paper,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import React, { useCallback, useMemo, useState } from 'react';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import {
  useDebouncedValue,
  useDisclosure,
  useViewportSize,
} from '@mantine/hooks';
import { getServiceLabel } from '../const/service_type_mapping.ts';
import { useBrandList } from '../hooks/brands/useBrandList.tsx';
import BrandCard from '../components/ui/brands/BrandCard.tsx';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import Fuse from 'fuse.js';
import { useQueryClient } from '@tanstack/react-query';
import { AddBrandModal } from '../components/modal/brands/AddBrandModal.tsx';

const fuseOptions = {
  keys: ['name'],
  threshold: 0.4,
  distance: 100,
  ignoreLocation: true,
};

const BrandListPage: React.FC = () => {
  const { brands, isLoading, error } = useBrandList();

  const [searchValue, setSearchValue] = useState<string>('');
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const queryClient = useQueryClient();

  const [
    addBrandModalOpened,
    { open: openAddBrandModal, close: closeAddBrandModal },
  ] = useDisclosure(false);

  const filteredBrands = useMemo(() => {
    if (!brands.length) return [];

    const fuse = new Fuse(brands, fuseOptions);

    const matchText = searchValue
      ? fuse.search(searchValue).map((result) => result.item)
      : brands;

    return matchText.filter((brand) => {
      return serviceTypes.length
        ? serviceTypes.every((selectedType) =>
            brand.services.some((s) => getServiceLabel(s) === selectedType),
          )
        : true;
    });
  }, [brands, searchValue, serviceTypes]);

  const totalItems = filteredBrands.length;

  const CONTAINER_MIN_WIDTH = 1440;
  const CONTAINER_MAX_WIDTH = 1920;
  const CARD_MIN_WIDTH = 210;
  const CARD_MIN_HEIGHT = 180;
  const CARD_MAX_WIDTH = 300;
  const CARD_MAX_HEIGHT = 210;
  const MIN_GAP = 9.5;
  const MAX_GAP = 17;

  const { width } = useViewportSize();
  const [debouncedWidth] = useDebouncedValue(width, 200);

  let ratio: number, card_width: number, card_height: number, gap: number;
  if (debouncedWidth >= CONTAINER_MAX_WIDTH) {
    card_width = CARD_MAX_WIDTH;
    card_height = CARD_MAX_HEIGHT;
    gap = MAX_GAP;
  } else if (debouncedWidth <= CONTAINER_MIN_WIDTH) {
    card_width = CARD_MIN_WIDTH;
    card_height = CARD_MIN_HEIGHT;
    gap = MIN_GAP;
  } else {
    ratio =
      (debouncedWidth - CONTAINER_MIN_WIDTH) /
      (CONTAINER_MAX_WIDTH - CONTAINER_MIN_WIDTH);
    card_width = Math.min(
      CARD_MAX_WIDTH,
      CARD_MIN_WIDTH + (CARD_MAX_WIDTH - CARD_MIN_WIDTH) * ratio,
    );
    card_height = Math.min(
      CARD_MAX_HEIGHT,
      CARD_MIN_HEIGHT + (CARD_MAX_HEIGHT - CARD_MIN_HEIGHT) * ratio,
    );
    gap = Math.min(MAX_GAP, MIN_GAP + (MAX_GAP - MIN_GAP) * ratio);
  }

  const itemsPerRow = 5;
  const itemsPerCol = 3;
  const itemsPerPage = itemsPerRow * itemsPerCol;

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const paginatedBrands = useMemo(() => {
    return filteredBrands.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBrands, startIndex, itemsPerPage]);

  const handleBrandAdded = useCallback(async () => {
    closeAddBrandModal();
    notifySuccess('新增品牌成功');
    setCurrentPage(1);
    await queryClient.invalidateQueries({
      queryKey: ['brands'],
      exact: true,
    });
  }, [queryClient, closeAddBrandModal]);

  const handleBrandUpdated = useCallback(async () => {
    notifySuccess('更新品牌成功');
    await queryClient.invalidateQueries({
      queryKey: ['brands'],
      exact: true,
    });
  }, [queryClient]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.currentTarget.value);
      setCurrentPage(1);
    },
    [],
  );

  const handleServiceTypeChange = useCallback((value: string[]) => {
    setServiceTypes(value);
    setCurrentPage(1);
  }, []);

  const handleAddBrandClick = useCallback(() => {
    openAddBrandModal();
  }, [openAddBrandModal]);

  if (isLoading)
    return (
      <>
        <LoadingOverlay
          visible={true}
          overlayProps={{ radius: 'sm', blur: 1 }}
          loaderProps={{
            color: 'rgb(67,154,255)',
            type: 'bars',
            size: 'sm',
          }}
        />
      </>
    );
  if (error) return <div style={{ marginLeft: 'xl' }}>載入品牌時發生錯誤</div>;

  return (
    <>
      {addBrandModalOpened && (
        <AddBrandModal
          opened={addBrandModalOpened}
          onClose={closeAddBrandModal}
          onSuccess={handleBrandAdded}
        />
      )}

      <Paper shadow="xl" radius="lg">
        <Group
          justify="space-between"
          p="24px 24px 20px 24px"
          style={{ borderBottom: 'none', flexShrink: 0 }}
        >
          <Title order={2}>品牌管理</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            size={'md'}
            variant="filled"
            onClick={handleAddBrandClick}
          >
            新增品牌
          </Button>
        </Group>

        <div style={{ flexShrink: 0, paddingInline: 24, paddingBottom: 20 }}>
          <Group align="center" gap="md" h={40}>
            <TextInput
              placeholder="搜尋品牌名稱"
              leftSection={<IconSearch size={16} />}
              value={searchValue}
              onChange={handleSearchChange}
              style={{ width: 321, height: 40 }}
            />
            <MultiSelect
              placeholder={serviceTypes.length ? '' : '服務類型'}
              data={[
                { value: '停車場', label: '停車場', disabled: false },
                { value: '加油站', label: '加油站', disabled: false },
                { value: '充電站', label: '充電站', disabled: false },
                { value: '保修廠', label: '保修廠', disabled: false },
                { value: '驗車場', label: '驗車場', disabled: false },
              ]}
              value={serviceTypes}
              onChange={handleServiceTypeChange}
              clearable
              style={{ width: 280, height: 40, fontSize: 'md' }}
            />
          </Group>
        </div>

        <Container
          fluid
          p={0}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginInline: 24,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(5, 1fr)`,
              gridTemplateRows: 'repeat(3, auto)',
              gap: gap,
              maxWidth: `${CARD_MAX_WIDTH * 5 + MAX_GAP * 4}px`,
              height: card_height * 3 + gap * 3,
              overflowY: 'auto',
              alignContent: 'start',
              justifyContent: 'start',
              justifyItems: 'center',
            }}
          >
            {paginatedBrands.map((brand) => (
              <div key={brand.id}>
                <BrandCard
                  brand={brand}
                  cardWidth={card_width}
                  cardHeight={card_height}
                  onBrandUpdated={handleBrandUpdated}
                />
              </div>
            ))}
          </div>
        </Container>

        <Group
          justify="space-between"
          align="center"
          pt={20 - gap}
          pb={20}
          pl={24}
          pr={24}
        >
          <Text size="sm" c="dimmed">
            顯示 {totalItems === 0 ? 0 : startIndex + 1} - {endIndex}{' '}
            筆品牌資訊，共 {totalItems} 筆
          </Text>
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            size="sm"
          />
        </Group>
      </Paper>
    </>
  );
};

export default BrandListPage;
